import type { Message } from '../types';

export function exportToJSON(messages: Message[], metadata: Record<string, unknown> = {}): string {
  const exportData = {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    metadata,
    messages: messages.map(m => ({
      id: m.id,
      sender: m.sender,
      text: m.text,
      timestamp: m.timestamp,
      attachments: m.attachments?.map(a => ({ name: a.name, type: a.type, size: a.size })),
      toolCalls: m.toolCalls,
      sources: m.sources,
    })),
    totalMessages: messages.length,
  };

  return JSON.stringify(exportData, null, 2);
}

export function exportToText(messages: Message[]): string {
  let text = 'Chat Conversation Export\n';
  text += `Exported: ${new Date().toLocaleString()}\n`;
  text += '='.repeat(50) + '\n\n';

  messages.forEach(msg => {
    text += `[${msg.timestamp}] ${msg.sender === 'user' ? 'You' : 'AI'}:\n`;
    text += `${msg.text}\n`;

    if (msg.sources && msg.sources.length > 0) {
      text += '\nSources:\n';
      msg.sources.forEach((s, i) => {
        text += `  ${i + 1}. ${s.title} - ${s.url}\n`;
      });
    }

    text += '\n' + '-'.repeat(50) + '\n\n';
  });

  return text;
}

export function exportToMarkdown(messages: Message[]): string {
  let md = '# Chat Conversation\n\n';
  md += `**Exported:** ${new Date().toLocaleString()}\n\n`;
  md += '---\n\n';

  messages.forEach(msg => {
    const sender = msg.sender === 'user' ? '**You**' : '**AI Assistant**';
    md += `### ${sender} (${msg.timestamp})\n\n`;
    md += `${msg.text}\n\n`;

    if (msg.sources && msg.sources.length > 0) {
      md += '**Sources:**\n\n';
      msg.sources.forEach((s, i) => {
        md += `${i + 1}. [${s.title}](${s.url})\n`;
        if (s.snippet) md += `   > ${s.snippet}\n`;
      });
      md += '\n';
    }

    md += '---\n\n';
  });

  return md;
}

export function downloadFile(content: string, filename: string, mimeType = 'text/plain'): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportConversation(messages: Message[], format = 'json'): void {
  const timestamp = new Date().toISOString().split('T')[0];

  switch (format) {
    case 'json': {
      const jsonContent = exportToJSON(messages, { format: 'json' });
      downloadFile(jsonContent, `chat-export-${timestamp}.json`, 'application/json');
      break;
    }
    case 'txt': {
      const textContent = exportToText(messages);
      downloadFile(textContent, `chat-export-${timestamp}.txt`, 'text/plain');
      break;
    }
    case 'md':
    case 'markdown': {
      const mdContent = exportToMarkdown(messages);
      downloadFile(mdContent, `chat-export-${timestamp}.md`, 'text/markdown');
      break;
    }
    default:
      console.error('Unsupported export format:', format);
  }
}
